import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Gem } from 'lucide-react'

// Множители выигрыша по цветам
const COLOR_MULTIPLIER = { red: 2, black: 2, green: 14 }
// Длительность анимации счётчика (мс)
const COUNTER_DURATION = 1000

// Анимированный счётчик суммы: плавно меняет число от startValue к targetValue за COUNTER_DURATION мс
const AnimatedSum = ({ value, isWinner, isLoser, isAnimating }) => {
  const [displayValue, setDisplayValue] = useState(value)
  const rafRef = useRef(null)
  const startTsRef = useRef(null)
  const startValRef = useRef(value)

  useEffect(() => {
    if (!isAnimating) {
      // Сброс — показываем реальное значение
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
      setDisplayValue(value)
      return
    }

    const from = value
    const to = isWinner ? value : 0
    startValRef.current = from
    startTsRef.current = null

    const tick = (ts) => {
      if (!startTsRef.current) startTsRef.current = ts
      const elapsed = ts - startTsRef.current
      const progress = Math.min(elapsed / COUNTER_DURATION, 1)
      // easeOutQuad для плавного замедления
      const eased = 1 - (1 - progress) * (1 - progress)
      const current = Math.round(from + (to - from) * eased)
      setDisplayValue(current)

      if (progress < 1) {
        rafRef.current = requestAnimationFrame(tick)
      }
    }

    rafRef.current = requestAnimationFrame(tick)
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current) }
  }, [isAnimating, isWinner, isLoser, value])

  // Для победителей после анимации уменьшения — показываем оригинал, умножение отображается отдельно
  return <>{Math.max(0, displayValue).toLocaleString()}</>
}

// Анимированный множитель суммы для победителей: value → value * multiplier
const AnimatedWinSum = ({ value, multiplier, isAnimating }) => {
  const target = value * multiplier
  const [displayValue, setDisplayValue] = useState(value)
  const rafRef = useRef(null)
  const startTsRef = useRef(null)

  useEffect(() => {
    if (!isAnimating) {
      setDisplayValue(value)
      return
    }

    startTsRef.current = null

    const tick = (ts) => {
      if (!startTsRef.current) startTsRef.current = ts
      const elapsed = ts - startTsRef.current
      const progress = Math.min(elapsed / COUNTER_DURATION, 1)
      const eased = 1 - (1 - progress) * (1 - progress)
      const current = Math.round(value + (target - value) * eased)
      setDisplayValue(current)

      if (progress < 1) {
        rafRef.current = requestAnimationFrame(tick)
      }
    }

    rafRef.current = requestAnimationFrame(tick)
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current) }
  }, [isAnimating, value, target])

  return <>{displayValue.toLocaleString()}</>
}

const DoubleBetsFeed = ({ bets = [], roundStatus, winColor }) => {
  // Затемнение колонок: при кручении все затемнены, при результате — только проигравшие
  const isSpinning = roundStatus === 'raffle'
  const isResult = ['result', 'finished'].includes(roundStatus)

  const getColumnDimmed = (colorKey) => {
    if (isSpinning) return true
    if (isResult && winColor && winColor !== colorKey) return true
    return false
  }
  const { t } = useTranslation()

  const colorColumns = [
    {
      key: 'red',
      cardClass: 'border-red-400/35 bg-red-500/12'
    },
    {
      key: 'black',
      cardClass: 'border-white/20 bg-slate-900/45'
    },
    {
      key: 'green',
      cardClass: 'border-emerald-400/35 bg-emerald-500/12'
    }
  ]

  const safeBets = Array.isArray(bets) ? bets : []

  return (
    <div>
      <div className="grid gap-3 lg:grid-cols-3">
        {colorColumns.map((column) => {
          const columnBets = safeBets.filter((bet) => String(bet.color || '') === column.key)

          const isColumnWinner = isResult && winColor === column.key
          const isColumnLoser = isResult && winColor && winColor !== column.key
          const multiplier = COLOR_MULTIPLIER[column.key] || 2

          return (
            <div key={column.key} className={`min-h-0 transition-opacity duration-500 ${getColumnDimmed(column.key) ? 'opacity-30' : 'opacity-100'}`}>
              <div className="space-y-2 max-h-[360px] overflow-y-auto pr-1">
                {columnBets.map((bet, index) => (
                  (() => {
                    const betItems = Array.isArray(bet.bet_items) ? bet.bet_items : []
                    const hasItems = betItems.length > 0
                    const betSum = Number(bet.sum || 0)

                    return (
                  <div
                    key={`${bet.chat_id || 'anon'}-${column.key}-${index}`}
                    className={`rounded-xl border p-3 ${column.cardClass}`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2 min-w-0">
                        <img
                          src={bet.ava}
                          alt={bet.name}
                          className="w-9 h-9 rounded-full object-cover border border-white/20"
                        />
                        <div className="min-w-0">
                          <p className="text-sm text-white font-semibold truncate">{bet.name || t('double.common.player', 'Игрок')}</p>
                        </div>
                      </div>

                      <span className={`inline-flex items-center gap-1 text-sm font-semibold ${isColumnWinner ? 'text-emerald-400' : isColumnLoser ? 'text-red-400' : 'text-accent-primary'}`}>
                        {isColumnWinner ? (
                          <AnimatedWinSum value={betSum} multiplier={multiplier} isAnimating={isResult} />
                        ) : isColumnLoser ? (
                          <AnimatedSum value={betSum} isWinner={false} isLoser={true} isAnimating={isResult} />
                        ) : (
                          betSum.toLocaleString()
                        )}
                        {' '}<Gem className="w-4 h-4" />
                      </span>
                    </div>

                    {hasItems ? (
                      <div className="mt-3">
                        <div className="grid grid-cols-3 gap-2">
                          {betItems.map((item, itemIndex) => (
                            <div
                              key={`${item.id || itemIndex}-${bet.chat_id || 'anon'}`}
                              className="rounded-md border border-white/10 bg-dark-primary/60 p-1.5 flex flex-col items-center justify-center text-center"
                            >
                              <img
                                src={item.img}
                                alt={item.name}
                                className="w-full h-12 object-contain mx-auto"
                                loading="lazy"
                              />
                              <p className="mt-1 text-[10px] text-accent-primary inline-flex items-center justify-center gap-0.5 w-full">
                                {Number(item.cost || 0).toLocaleString()} <Gem className="w-3 h-3" />
                              </p>
                            </div>
                          ))}

                          {Number(bet.crystal || 0) > 0 && (
                            <div className="rounded-md border border-cyan-300/30 bg-cyan-500/10 p-1.5 flex flex-col items-center justify-center text-center">
                              <div className="w-full h-12 flex items-center justify-center">
                                <Gem className="w-6 h-6 text-cyan-200" />
                              </div>
                              <p className="mt-1 text-[10px] text-cyan-100 inline-flex items-center justify-center gap-0.5 w-full">
                                {Number(bet.crystal || 0).toLocaleString()} <Gem className="w-3 h-3" />
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    ) : null}

                    {bet.win_skin && (
                      <div className="mt-3 rounded-lg border border-emerald-300/30 bg-emerald-500/10 p-2.5">
                        <p className="text-[11px] text-emerald-200 mb-2">
                          {t('double.feed.winSkin', 'Выиграл скин')}
                        </p>
                        <div className="flex items-center gap-2">
                          <img
                            src={bet.win_skin.img}
                            alt={bet.win_skin.name}
                            className="w-12 h-12 object-contain"
                            loading="lazy"
                          />
                          <div className="min-w-0">
                            <p className="text-xs text-white font-medium truncate">{bet.win_skin.name}</p>
                            <p className="text-xs text-emerald-200 inline-flex items-center gap-1">
                              {Number(bet.win_skin.cost || 0).toLocaleString()} <Gem className="w-3 h-3" />
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                    )
                  })()
                ))}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default DoubleBetsFeed
